import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';
import { devicePrinterHeaders } from '@/stores/device-printer-store';

export type PrinterKind = 'LABEL_ZPL' | 'DOCUMENT_PDF';
export type PrintJobStatus = 'QUEUED' | 'SENT' | 'DONE' | 'FAILED' | 'EXPIRED';

export type LabelOrientation = 'UPRIGHT' | 'ROTATED';
export type LabelLayoutStyle = 'CLASSIC' | 'FILL';
export type LabelStockSize = '4x2' | '4x3' | '4x6';

export interface Printer {
  id: string;
  name: string;
  printNodeId: number;
  kind: PrinterKind;
  model: string | null;
  description: string | null;
  computerName: string | null;
  /** Printhead resolution. Seeded from the driver name at sync; a guess until verified against hardware. */
  dpi: number;
  /** Label stock loaded. CHECK-constrained in the DB, so the union is trustworthy. */
  labelSize: LabelStockSize;
  /** UPRIGHT reads across the width; ROTATED along the length (built-in layouts only). */
  labelOrientation: LabelOrientation;
  /** CLASSIC = pre-geometry output, byte-identical; FILL = reviewed fill layout. */
  labelLayout: LabelLayoutStyle;
  createdAt: string;
  updatedAt: string;
}

export interface UpdatePrinterGeometryParams {
  printerId: string;
  dpi?: number;
  labelSize?: LabelStockSize;
  labelOrientation?: LabelOrientation;
  labelLayout?: LabelLayoutStyle;
}

export interface PrintJob {
  id: string;
  printerId: string;
  status: PrintJobStatus;
  title: string;
  printNodeJobId: number | null;
  error: string | null;
  createdAt: string;
  sentAt: string | null;
}

export function useListPrinters() {
  return useQuery({
    queryKey: queryKeys.printers.list(),
    queryFn: async () => {
      const { data } = await apiClient.get<Printer[]>('/api/printing/printers');
      return data;
    },
  });
}

export interface PrintContainerLabelsParams {
  warehouseId: string;
  items: Array<{ id: string; number: string }>;
  copies?: number;
  labelSize?: LabelStockSize;
  title?: string;
}

/**
 * Server-side container-label print (Phase 4). The server resolves the
 * printer and authors the ZPL for ITS geometry — the browser cannot know
 * printer geometry, which is why the old build-and-POST-raw path printed at
 * 68% scale on a 300dpi head. The client builder remains for PREVIEW only.
 */
export function usePrintContainerLabels() {
  return useMutation({
    mutationFn: async (params: PrintContainerLabelsParams) => {
      const { data } = await apiClient.post<PrintJob>(
        '/api/printing/container-labels',
        params,
        { headers: devicePrinterHeaders() },
      );
      return data;
    },
  });
}

/**
 * Auto-print mode after picker assignment, resolved per warehouse (WAREHOUSE
 * override beats the GLOBAL default; default ASK). The same three values govern
 * both the pick list and the pick label, each via its own setting:
 *  - AUTO: print automatically, server-side, on assignment.
 *  - ASK:  the UI prompts the manager to print after assignment.
 *  - OFF:  nothing prints on assignment.
 * Shared by the Settings cards and the assign-dialog prompt.
 */
export type PickListAutoPrintMode = 'AUTO' | 'ASK' | 'OFF';

export interface PrintPickListParams {
  /**
   * Sales order to print for. The server derives every pick order from the SO
   * (one slip/label each), so the caller never enumerates pick order ids. An SO
   * with no pick orders yet prints a single pre-assignment slip / label.
   */
  salesOrderId: string;
}

export interface PrintPickListJob {
  pickOrderId: string;
  printJobId: string;
  printNodeJobId: number | null;
  status: PrintJobStatus;
}

export interface PrintPickListResponse {
  jobs: PrintPickListJob[];
  printerName: string;
}

/**
 * Render pick list(s) as PDF (one per pick order) and dispatch them to the
 * warehouse's document printer via PrintNode. The warehouse is resolved
 * server-side from the sales order, so the caller never sends a warehouseId.
 * Backs the ASK-mode "Print pick list?" prompt. Mirrors the label-dispatch mutations — no
 * cache invalidation (printing doesn't mutate SO/pick-order state).
 */
export function usePrintPickList() {
  return useMutation({
    mutationFn: async (params: PrintPickListParams) => {
      const { data } = await apiClient.post<PrintPickListResponse>(
        '/api/printing/pick-lists',
        params,
        { headers: devicePrinterHeaders() },
      );
      return data;
    },
  });
}

/**
 * Print one order-number label per pick order on the warehouse label printer
 * via PrintNode. Same request/response shape as usePrintPickList; backs the
 * ASK-mode "Print labels?" prompt.
 */
export function usePrintPickLabels() {
  return useMutation({
    mutationFn: async (params: PrintPickListParams) => {
      const { data } = await apiClient.post<PrintPickListResponse>(
        '/api/printing/pick-labels',
        params,
        { headers: devicePrinterHeaders() },
      );
      return data;
    },
  });
}

/**
 * Outcome of a design-assembly print. Absence is a normal result, not an error:
 * most orders are not design orders, and a design order's document may not have
 * composed yet — so the server answers 200 and the caller renders the message.
 */
export type DesignAssemblyPrintStatus =
  | 'PRINTED'
  | 'NOT_A_DESIGN_ORDER'
  | 'NOT_READY'
  | 'ALREADY_PRINTED';

export interface PrintDesignAssemblyResponse {
  status: DesignAssemblyPrintStatus;
  printJobId?: string;
  printNodeJobId?: number;
  printerName?: string;
  /** When the order was last printed — set on ALREADY_PRINTED and PRINTED. */
  printedAt?: string;
  /**
   * The WMS code stamped onto this printout (`LS-DD-…`). Only on PRINTED when
   * the warehouse has `picking.design_document_scan_required` on.
   */
  code?: string;
}

/**
 * Whether design-assembly printing is configured at all.
 *
 * The picking station hides the control when this is false rather than showing
 * a button that always refuses — the Shopify credentials are unwired until the
 * secrets land, and without this every picker sees a dead control. Config-only
 * on the server, so it is cheap; cached generously because it changes only on
 * a deploy.
 */
export function useDesignAssemblyEnabled(enabled = true) {
  return useQuery({
    // Skipped for a pick order with no sales order (a transfer pick): it can
    // never use the control, so the probe would be a request per mount for an
    // answer that cannot matter.
    enabled,
    queryKey: queryKeys.printers.designAssemblyEnabled(),
    queryFn: async () => {
      const { data } = await apiClient.get<{ enabled: boolean }>(
        '/api/printing/design-assembly/enabled',
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Print a design order's assembly document — plan and wall-elevation drawings
 * plus the unpriced parts list — on the warehouse's design drawings printer,
 * else this station's document printer.
 *
 * The server correlates the sales order back to Shopify itself (via the
 * customer order number the Acumatica shipment carries) and reads the
 * design/pdf_links metafield, so the caller sends only the SO id and, on a
 * deliberate reprint, `reprint: true`. It invalidates nothing — the one piece
 * of SO state it writes, `designAssemblyPrintedAt`, is read by no cached
 * query; the server returns the stamp inline for the confirm dialog.
 */
export function usePrintDesignAssembly() {
  return useMutation({
    mutationFn: async (params: {
      salesOrderId: string;
      /** Print again despite the once-per-order stamp. */
      reprint?: boolean;
    }) => {
      const { data } = await apiClient.post<PrintDesignAssemblyResponse>(
        '/api/printing/design-assembly',
        params,
        // With the drawings stamp on, resolve + full download + stamp +
        // PrintNode runs ~31 s — past the client-wide 30 s. This call only.
        { headers: devicePrinterHeaders(), timeout: 60_000 },
      );
      return data;
    },
  });
}

export interface SyncPrintersResult {
  created: number;
  updated: number;
  total: number;
  /** Printers retired this sync — gone from the PrintNode roster. */
  deactivated: number;
}

/**
 * PATCH a printer's label geometry. Invalidates the whole printers key space —
 * the roster table and WarehouseDefaultsCard share it, so one invalidation
 * refreshes both.
 */
export function useUpdatePrinterGeometry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ printerId, ...body }: UpdatePrinterGeometryParams) => {
      const { data } = await apiClient.patch<Printer>(
        `/api/printing/printers/${printerId}`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.printers.all });
    },
  });
}

export function useSyncPrinters() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<SyncPrintersResult>(
        '/api/printing/printers/sync',
        {},
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.printers.all });
    },
  });
}

const NON_TERMINAL: PrintJobStatus[] = ['QUEUED', 'SENT'];

export function usePrintJobs(printerId?: string, limit?: number) {
  return useQuery({
    queryKey: queryKeys.printJobs.list(printerId, limit),
    queryFn: async () => {
      const { data } = await apiClient.get<PrintJob[]>('/api/printing/jobs', {
        params: { printerId, limit },
      });
      return data;
    },
    enabled: !!printerId,
    // Live status: poll every 5s only while a visible job is still non-terminal
    // (QUEUED/SENT). Once everything is DONE/FAILED/EXPIRED, stop polling so the
    // panel reflects the webhook outcome without burning requests forever.
    refetchInterval: (query) => {
      const jobs = query.state.data as PrintJob[] | undefined;
      const hasPending = jobs?.some((j) => NON_TERMINAL.includes(j.status));
      return hasPending ? 5_000 : false;
    },
    // Don't poll an unattended/backgrounded tab (this is TanStack's default;
    // set explicitly so it survives a future global default change).
    refetchIntervalInBackground: false,
  });
}
